import React, { useEffect, useCallback } from 'react';
import { useMappedState, useDispatch } from 'redux-react-hook';
import { Link } from '@reach/router';
import { linkTo } from 'utils/links';

// Redux
import { getToken } from 'store/modules/auth/selectors';
import { getQueue, getPlayingSong, isPlaying, isShaffled, getRepeat, isMuted } from 'store/modules/player/selectors';
import { play, toggleMute } from 'store/modules/player/actions';
import { countPlay } from 'store/modules/music/actions';

// Components
import Dropdown from 'components/Dropdown';
import Image from 'components/Image';
import Folder from 'components/Icon/Folder';
// import Globe from 'components/Icon/Globe';
// import Heart from 'components/Icon/Heart';
import Volume from 'components/Icon/Volume';
import Mute from 'components/Icon/Mute';
import Controls from './Controls';
import PlayerButton from './PlayerButton';
import ProgressBar from './ProgressBar';

const isAmazonS3 = /s3\.amazonaws\.com/;

const mapState = state => ({
  token: getToken(state),
  playing: isPlaying(state),
  repeat: getRepeat(state),
  song: getPlayingSong(state),
  queue: getQueue(state),
  shaffled: isShaffled(state),
  muted: isMuted(state),
});

export default function Player() {
  const { queue, song, playing, token, repeat, shaffled, muted } = useMappedState(mapState);
  const dispatch = useDispatch();
  const muteClick = useCallback(() => {
    dispatch(toggleMute());
  }, [muted])
  useEffect(() => {
    // For S3 we don't count those plays automatically,
    // therefore we need to manually call the tracking endpoint
    if (isAmazonS3.test(song.sound)) {
      dispatch(countPlay(song));
    }
  }, [song]);
  const VolumeIcon = muted ? Mute : Volume;

  return (
    <div className="Player fixed bottom-0 left-0 w-screen bg-neutral-darker z-30 shadow" style={{ height: '70px' }}>
      <div className="flex px-2 py-2 md:px-8">
        <div className="hidden w-1/4 pr-8 md:flex">
          <Link to={linkTo(`${linkTo('/albums/:slug', song.album)}/:slug`, song)}>
            <Image src={song.album && song.album.image} alt={song.title} title={song.title} className="rounded bg-neutral" style={{width: '54px', height: '54px'}}/>
          </Link>
          <div className="flex-1 pl-2 flex flex-col justify-center w-1/4">
            <h4 className="text-default font-sans font-normal truncate" title={song.title}>
              <Link to={linkTo(`songs/:slug`, song)} className="hover:underline">
                {song.title}
              </Link>  
            </h4>
            <p className="text-secondary font-sans truncate" title={song.author && song.author.name}>
              <Link to={linkTo(`members/profiles/:slug`, song.author)} className="hover:underline">
                {song.author && song.author.name}
                {!song.author && '---'}
              </Link>
            </p>
          </div>
        </div>
        <div className="w-full md:w-1/2">
          <Controls playing={playing} queue={queue} current={song} repeat={repeat} shaffled={shaffled} />
          <ProgressBar
            song={song}
            queue={queue}
            playing={playing}
            token={token}
            repeat={repeat}
            muted={muted}
            className="flex"
          />
        </div>
        <div className="hidden w-1/4 md:flex md:items-center">
          <div className="w-full md:flex md:justify-end" style={{height: '25px'}}>
            <div className="px-2 mx-1 text-secondary hover:text-default hover:border hover:border-white-darkest hover:bg-white-darkest">
              <Dropdown
                options={queue}
                valueField="id"
                labelField="title"
                selections={[song]}
                onChangeSelection={(name, sng) => dispatch(play(sng, queue))}
                anchor="top-right"
                caret={false}
              >
                <Folder width={20} height={20} className="fill-current" />
              </Dropdown>
            </div>
            {/* <PlayerButton>
              <Globe width={20} height={20} className="fill-current" />
            </PlayerButton>
            <PlayerButton>
              <Heart width={20} height={20} className="fill-current" />
            </PlayerButton> */}
            <PlayerButton onClick={muteClick}>
              <VolumeIcon width={25} height={25} className="fill-current" />
            </PlayerButton>
          </div>
        </div>
      </div>
    </div>
  );
}
